/**
 * @file     PanelFrameFallback.tsx
 * @purpose  The results panel's frame while the map view chunk loads: the same
 *           place as the live panel (right 45% on desktop and tablet portrait,
 *           a radius 28 sheet below the map's default snap on phones), with the
 *           server-rendered intro and settled-height tile placeholders. Keeps
 *           the page H1 in the SSR HTML and in place when the panel mounts.
 */
'use client';

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { SkeletonBlock } from '@/design-system/skeletons/SkeletonBlock';
import { layoutTokens } from '@/design-system/tokens/layout';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { TileSkeletonGrid } from './PanelSkeletons';
import {
  DESKTOP_MAP_WIDTH_PCT,
  MOBILE_MAP_DEFAULT_PCT,
  MOBILE_PANEL_STRIP_HEIGHT,
  PANEL_HEADER_SX,
} from '../panelLayout';

const sideBySide = {
  top: 0,
  left: 'auto',
  width: `${100 - DESKTOP_MAP_WIDTH_PCT}%`,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderLeft: hairline,
  boxShadow: 'none',
} as const;

export function PanelFrameFallback({ intro }: { intro?: ReactNode }) {
  const { t } = useTranslation();
  const sheetRadius = `${layoutTokens.radius.xl}px`;
  return (
    <Box
      data-panel-fallback
      sx={{
        position: 'absolute',
        top: `${MOBILE_MAP_DEFAULT_PCT}%`,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: hubColors.surfaceCard,
        borderTopLeftRadius: sheetRadius,
        borderTopRightRadius: sheetRadius,
        boxShadow: `0 -10px 28px ${hubColors.scrim}`,
        '@media (min-width: 900px)': sideBySide,
        '@media (min-width: 768px) and (orientation: portrait)': sideBySide,
      }}
    >
      <Box
        aria-hidden
        sx={{
          height: MOBILE_PANEL_STRIP_HEIGHT,
          flexShrink: 0,
          '@media (min-width: 900px)': { display: 'none' },
          '@media (min-width: 768px) and (orientation: portrait)': { display: 'none' },
        }}
      />
      <Box sx={{ ...PANEL_HEADER_SX, flexShrink: 0, display: 'flex', flexDirection: 'column', borderBottom: hairline }}>
        {intro}
        <SkeletonBlock height={48} radius={layoutTokens.radius.md} />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: PANEL_HEADER_SX.px, pt: 4 }}>
        <TileSkeletonGrid count={4} label={t('pages.properties.map.panel.loading', { defaultValue: 'Loading listings' })} />
      </Box>
    </Box>
  );
}
